import { useCallback, useEffect } from 'react';

import {
  DynamicAssessmentFlow,
  type DynamicAssessmentDoneReason,
  type DynamicAssessmentResult,
} from '@/components/DynamicAssessmentFlow';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

/**
 * The onboarding dynamic-survey step (`/step/dynamic-assessment`) — the
 * `onb_use_dynamic_assessment` TEST arm only. The resolver routes the whole
 * survey region here (health conditions → goals → AOM screeners → US state → lab
 * preference → diabetes decision → pre-diabetes → phq/gad, served as server-driven
 * flows). On a clean finish it advances to `/step/assigned-screeners`, where the
 * legacy flow mops up any screener dynamic assessment doesn't support yet
 * (excluding the migrated keys) — see `dynamicSurveyNextStep`.
 *
 * Control (or a failed flag read, which fails open to control) never reaches this
 * route: the resolver starts control at `/step/health-conditions` and the legacy
 * screeners run at `/step/assigned-screeners`.
 *
 * A dynamic-pass ERROR latches `markDynamicAssessmentErrored` into the rules
 * engine. All-or-nothing: rather than falling back to the legacy walk, the
 * resolver abandons the whole survey region and skips to the program region, so
 * the driver navigates this screen away.
 *
 * A phq_gad_v1 crisis terminal is deferred (`deferCrisisProgram`) rather than
 * routed immediately — the leftover legacy screeners and state-compliance still
 * run; goToNextStep enters the crisis program at survey exhaustion. But a crisis
 * outcome captured before a LATER, unrelated failure (e.g. the next pass's
 * `/flows/` read throwing after phq_gad_v1's crisis terminal already came back —
 * phq_gad_v1 is last, so the likeliest spot) must NOT be lost to the error
 * branch: since the errored abandon skips the survey region, the
 * defer-until-exhaustion consumption never runs, so we enter the crisis program
 * immediately (`enterCrisisProgram`) instead of deferring.
 */
export function DynamicAssessmentScreen() {
  const {
    dynamicAssessmentEnabled,
    ensureDynamicAssessment,
    dynamicAssessmentErrored,
    markDynamicAssessmentErrored,
    deferCrisisProgram,
    enterCrisisProgram,
    setDynamicCompletedFlowKeys,
  } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();

  const handleDynamicDone = useCallback(
    (reason: DynamicAssessmentDoneReason, result?: DynamicAssessmentResult) => {
      // `DynamicAssessmentFlow` passes `crisisProgramUrns` regardless of reason,
      // so a real crisis outcome can arrive alongside `reason === 'error'`.
      // Handle it before the error branch's early return, which would otherwise
      // discard it and route the member to the normal program region without the
      // crisis-line routing they qualified for.
      const crisisUrns = result?.crisisProgramUrns;
      if (reason === 'error') {
        if (crisisUrns && crisisUrns.length > 0) {
          // The errored abandon skips the survey region, so goToNextStep's
          // defer-until-exhaustion consumption never runs — enter the crisis
          // program now (sets requiresCrisisLine + marks the survey complete) so
          // the resolver routes there instead of the normal program region.
          enterCrisisProgram(crisisUrns);
          return;
        }
        // All-or-nothing: latch the error in the rules engine. The resolver
        // abandons the survey region and skips to the program region, so the
        // driver navigates this screen away (no legacy fallback).
        markDynamicAssessmentErrored();
        return;
      }
      // Clean finish: a crisis outcome from phq_gad_v1 is deferred until survey
      // exhaustion (the legacy screeners + state-compliance still run first).
      if (crisisUrns && crisisUrns.length > 0) {
        deferCrisisProgram(crisisUrns);
      }
      // Latch which flows actually completed so the remaining survey steps skip a
      // standalone pre-diabetes / phq-gad screen ONLY when its dynamic flow ran —
      // an unpublished/abandoned flow isn't listed, so its standalone screener
      // still runs rather than being silently dropped.
      setDynamicCompletedFlowKeys(result?.completedFlowKeys ?? []);
      // Advance to the legacy assigned-screeners step (the unmigrated-screener
      // safety net) — dynamicSurveyNextStep owns the survey-region order.
      goToNextStep({ from: '/step/dynamic-assessment' });
    },
    [
      markDynamicAssessmentErrored,
      deferCrisisProgram,
      enterCrisisProgram,
      setDynamicCompletedFlowKeys,
      goToNextStep,
    ]
  );

  // Survey-region entry: read the `onb_use_dynamic_assessment` experiment
  // (deduped; the driver usually read it already to pick the survey start).
  useEffect(() => {
    ensureDynamicAssessment();
  }, [ensureDynamicAssessment]);

  // An errored dynamic pass abandons the survey region: the resolver skips to the
  // program region and the driver is navigating this screen away. Hold a spinner
  // for the render between the latch and the redirect.
  if (dynamicAssessmentErrored) {
    return <LoadingScreen />;
  }

  const isTestArm =
    dynamicAssessmentEnabled.status === 'ready' && dynamicAssessmentEnabled.value === true;

  // Until the flag resolves, hold the spinner. If it resolves to anything but the
  // test arm (control, or an errored read that fails open to control), this route
  // shouldn't have been reached — hold the spinner and let the route group's
  // forward-redirect send the user to their real `currentRoute`.
  if (!isTestArm) {
    return <LoadingScreen />;
  }

  return <DynamicAssessmentFlow onDone={handleDynamicDone} />;
}
